import React from 'react';
import {
  styled,
  FormControl,
  Box,
  TextField,
  Slider,
  Stack,
} from '@mui/material';
import {
  designSystemToken,
  Body,
  SwitchButton,
  Icon,
  Button,
} from '@lightricks/react-design-system';
import StyledColorPicker from '@contentlab/components/styled-color-picker';
import {AnimationKind, Alignment} from '@contentlab/types/captions';
import translate from '@/utils/translate';

// Common components

const StyledSelectContainer = styled(FormControl)(() => ({
  'height': '40px',
  '& .MuiInputLabel-root': {
    display: 'none',
  },
  '& .MuiOutlinedInput-root': {
    'backgroundColor': designSystemToken('semantic.bg.primary'),
    'borderRadius': '8px',
    'height': '40px',
    'fontSize': designSystemToken('typography.body.md.fontSize'),
    'fontFamily': designSystemToken('typography.body.md.fontFamily'),
    'fontWeight': designSystemToken('typography.body.md.fontWeight'),
    'lineHeight': designSystemToken('typography.body.md.lineHeight'),
    'color': designSystemToken('semantic.fg.primary'),
    '& fieldset': {
      borderColor: designSystemToken('semantic.bg.neutral-secondary'),
      borderWidth: '1px',
    },
    '&:hover fieldset': {
      borderColor: designSystemToken('semantic.bg.neutral-secondary'),
    },
    '&.Mui-focused fieldset': {
      borderColor: designSystemToken('semantic.bg.neutral-secondary'),
    },
    '& .MuiSelect-select': {
      padding: '12px',
      display: 'flex',
      alignItems: 'center',
      minHeight: 'auto',
    },
  },
}));

const SectionLabel = styled(Body)(() => ({
  marginBottom: '16px',
}));

const StyledEditableText = styled(TextField)(() => ({
  '& .MuiInputBase-root': {
    height: 'auto',
    width: '35px',
    fontSize: designSystemToken('typography.body.md.fontSize'),
    fontFamily: designSystemToken('typography.body.md.fontFamily'),
    fontWeight: designSystemToken('typography.body.md.fontWeight'),
    lineHeight: designSystemToken('typography.body.md.lineHeight'),
    backgroundColor: 'transparent',
    border: 'none',
    color: designSystemToken('semantic.fg.secondary'),
  },
  '& .MuiInputBase-input': {
    textAlign: 'left',
    padding: 0,
    border: 'none',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
}));

const StyledSlider = styled(Slider)(() => ({
  'color': designSystemToken('semantic.bg.neutral'),
  'height': '2px',
  '& .MuiSlider-thumb': {
    'backgroundColor': designSystemToken('semantic.bg.neutral'),
    'width': '12px !important',
    'height': '12px !important',
    'marginLeft': '-6px',
    'borderRadius': '50%',
    'border': 'none',
    'transform': 'translateY(-50%)',
    'top': '50%',
    '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
      boxShadow: 'none !important',
    },
    '&:before': {
      display: 'none',
    },
    '&:after': {
      display: 'none',
    },
  },
  '& .MuiSlider-track': {
    backgroundColor: designSystemToken('semantic.bg.neutral'),
    height: '2px',
  },
  '& .MuiSlider-rail': {
    backgroundColor: designSystemToken('semantic.bg.neutral-secondary'),
    height: '2px',
  },
}));

const ColorPickerBox = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  backgroundColor: designSystemToken('semantic.bg.primary'),
  border: `1px solid ${designSystemToken('semantic.bg.neutral-secondary')}`,
  borderRadius: '8px',
  padding: '8px 16px',
  height: '40px',
  boxSizing: 'border-box',
}));

const DividerLine = styled(Box)(() => ({
  height: '1px',
  backgroundColor: designSystemToken('semantic.bg.neutral-inverse'),
  width: '100%',
  margin: '24px 0px',
}));

const StyledToggleContainer = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
}));

const PositionButtonGroup = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
  gap: '16px',
  width: '100%',
}));

const PositionAlignButton = styled(Button)<{selected?: boolean}>(
  ({selected}) => ({
    'flex': 1,
    'height': '40px',
    'backgroundColor': designSystemToken('semantic.bg.primary'),
    'border': selected
      ? `1px solid ${designSystemToken('semantic.bg.neutral')}`
      : `1px solid ${designSystemToken('semantic.bg.neutral-secondary')}`,
    'minWidth': 'auto',
    'padding': '16px',
    'borderRadius': '8px',
    '&:hover': {
      backgroundColor: designSystemToken('semantic.bg.primary'),
      opacity: 0.8,
    },
  })
);

// Common values for the style editor

// Single fallback font that's guaranteed to be available and loaded
const FALLBACK_FONT_FAMILIES = [
  {
    id: 'arial',
    name: 'Arial',
    downloadUrls: {
      regular: '',
      bold: '',
      italic: '',
    },
  },
];

const FONT_SIZE_OPTIONS = [
  {label: '18px', value: 18},
  {label: '24px', value: 24},
  {label: '30px', value: 30},
  {label: '36px', value: 36},
  {label: '48px', value: 48},
  {label: '60px', value: 60},
  {label: '72px', value: 72},
  {label: '96px', value: 96},
  {label: '104px', value: 104},
  {label: '120px', value: 120},
  {label: '136px', value: 136},
  {label: '148px', value: 148},
  {label: '160px', value: 160},
];

const ANIMATION_OPTIONS = [
  {value: null, label: 'None'},
  {value: AnimationKind.FADE_IN, label: 'Fade In'},
  {value: AnimationKind.SLIDE_IN, label: 'Slide In'},
  {value: AnimationKind.POP_IN, label: 'Pop In'},
  {value: AnimationKind.WORD_BY_WORD, label: 'Word by Word'},
  {value: AnimationKind.KARAOKE_HIGHLIGHT, label: 'Karaoke Highlight'},
];

// Helper functions

export function getHexWithoutHash(hex?: string): string {
  return hex?.replace('#', '') || '';
}

function getValidSubHexWithoutHash(hex: string): string {
  const cleanHex = getHexWithoutHash(hex);
  const validHex = cleanHex.replace(/[^0-9a-fA-F]/g, '');
  return validHex.slice(0, 6);
}

function getValidPaddedHexWithoutHash(hex: string): string {
  const validHex = getValidSubHexWithoutHash(hex);
  if (validHex.length >= 0 && validHex.length < 6) {
    return validHex.padEnd(6, '0');
  }
  return validHex;
}

const createHexInputHandlers = (
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
) => {
  const createSyntheticEvent = (originalEvent: any, hexValue: string) => {
    const syntheticEvent = {
      ...originalEvent,
      target: {
        ...originalEvent.target,
        value: `#${hexValue}`,
      },
    };
    onChange(syntheticEvent as React.ChangeEvent<HTMLInputElement>);
  };

  const handleHexInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const hexValue = getValidSubHexWithoutHash(e.target.value);
    createSyntheticEvent(e, hexValue);
  };

  const handleHexInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const hexValue = getValidPaddedHexWithoutHash(e.target.value);
    createSyntheticEvent(e, hexValue);
  };

  return {handleHexInputChange, handleHexInputBlur};
};

interface EditableHexTextFieldProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

function EditableHexTextField({value, onChange}: EditableHexTextFieldProps) {
  const {handleHexInputChange, handleHexInputBlur} =
    createHexInputHandlers(onChange);

  return (
    <TextField
      value={getHexWithoutHash(value)}
      onChange={handleHexInputChange}
      onBlur={handleHexInputBlur}
      size="small"
      placeholder="000000"
      sx={{
        '& .MuiInputBase-root': {
          height: 'auto',
          width: '60px',
          fontSize: '14px',
          fontFamily: 'Brown LL, monospace',
          backgroundColor: 'transparent',
          border: 'none',
          color: designSystemToken('semantic.fg.secondary'),
        },
        '& .MuiInputBase-input': {
          textAlign: 'center',
          padding: 0,
          fontFamily: 'Brown LL, monospace',
        },
        '& .MuiOutlinedInput-notchedOutline': {
          border: 'none',
        },
        '& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline': {
          border: 'none',
        },
        '& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
          border: 'none',
        },
      }}
    />
  );
}

const EditableHexInput = styled(TextField)(() => ({
  '& .MuiInputBase-root': {
    height: '24px',
    width: '60px',
    fontSize: '12px',
    fontFamily: 'Brown LL, monospace',
    backgroundColor: 'transparent',
    border: `1px solid ${designSystemToken('semantic.bg.neutral-secondary')}`,
    borderRadius: '4px',
    color: designSystemToken('semantic.fg.secondary'),
  },
  '& .MuiInputBase-input': {
    textAlign: 'center',
    padding: '2px 4px',
    fontFamily: 'Brown LL, monospace',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '& .MuiInputBase-root:hover': {
    borderColor: designSystemToken('semantic.bg.neutral'),
  },
  '& .MuiInputBase-root.Mui-focused': {
    borderColor: designSystemToken('semantic.bg.neutral'),
  },
}));

interface KeyboardNavigableInputProps {
  value: number | string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  min: number;
  max: number;
  step: number;
  suffix?: string;
}

function KeyboardNavigableInput({
  value,
  onChange,
  min,
  max,
  step,
  suffix = '',
}: KeyboardNavigableInputProps) {
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') {
      return;
    }

    e.preventDefault();
    const currentValue =
      typeof value === 'string'
        ? parseFloat(value.replace(suffix, '')) || 0
        : value;

    const newValue =
      e.key === 'ArrowUp'
        ? Math.min(max, currentValue + step)
        : Math.max(min, currentValue - step);

    const syntheticEvent = {
      ...e,
      target: {
        ...e.target,
        value: suffix ? `${newValue}${suffix}` : newValue.toString(),
      },
    } as any;

    onChange(syntheticEvent as React.ChangeEvent<HTMLInputElement>);
  };

  return (
    <StyledEditableText
      type="text"
      value={value}
      onChange={onChange}
      onKeyDown={handleKeyDown}
      size="small"
      sx={{
        width: '17px',
        color: designSystemToken('semantic.fg.secondary'),
      }}
    />
  );
}
interface ColorPickerProps {
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  sx?: any;
}

function ColorPicker({label, value, onChange, sx}: ColorPickerProps) {
  return (
    <ColorPickerBox sx={sx}>
      <Body size="md">{translate(label)}</Body>
      <Stack direction="row" alignItems="center" gap="8px">
        <EditableHexTextField value={value} onChange={onChange} />
        <StyledColorPicker value={value} onChange={onChange} size="small" />
      </Stack>
    </ColorPickerBox>
  );
}

const SliderContainer = styled(Box)(() => ({
  backgroundColor: designSystemToken('semantic.bg.primary'),
  border: `1px solid ${designSystemToken('semantic.bg.neutral-secondary')}`,
  borderRadius: '16px',
  padding: '0px',
  height: '66px',
  position: 'relative',
}));

const SliderIconContainer = styled(Box)(() => ({
  position: 'absolute',
  left: '16px',
  top: '50%',
  transform: 'translateY(-50%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1,
}));

const SliderLabelContainer = styled(Box)(() => ({
  position: 'absolute',
  top: '12px',
  right: '16px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

const SliderWrapper = styled(Box)(() => ({
  position: 'absolute',
  bottom: '8px',
  right: '16px',
  height: '32px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}));

const ColorPickerText = styled(Body)(() => ({
  color: designSystemToken('semantic.fg.secondary'),
}));

interface LabeledSliderProps {
  label: string;
  value: number;
  displayValue: string;
  min: number;
  max: number;
  step: number;
  onChange: (value: number) => void;
  iconName: React.ComponentProps<typeof Icon>['name'];
  iconColor?: string;
  sx?: any;
}

function LabeledSlider({
  label,
  value,
  displayValue,
  min,
  max,
  step,
  onChange,
  iconName,
  iconColor,
  sx,
}: LabeledSliderProps) {
  const labelStartPosition = '64px';

  return (
    <SliderContainer sx={sx}>
      <SliderIconContainer
        sx={{
          color: iconColor || designSystemToken('semantic.fg.secondary'),
        }}
      >
        <Icon name={iconName} size="medium" appearance="neutral" />
      </SliderIconContainer>

      <SliderLabelContainer
        sx={{
          left: labelStartPosition,
        }}
      >
        <ColorPickerText size="sm">{label}</ColorPickerText>
        <ColorPickerText size="sm">{displayValue}</ColorPickerText>
      </SliderLabelContainer>

      <SliderWrapper
        sx={{
          left: labelStartPosition,
        }}
      >
        <StyledSlider
          value={value}
          min={min}
          max={max}
          step={step}
          onChange={(_, newValue) => onChange(newValue as number)}
        />
      </SliderWrapper>
    </SliderContainer>
  );
}

export {
  StyledSelectContainer,
  SectionLabel,
  StyledSlider,
  EditableHexInput,
  EditableHexTextField,
  KeyboardNavigableInput,
  ColorPickerBox,
  ColorPicker,
  LabeledSlider,
  DividerLine,
  SwitchButton,
  StyledToggleContainer,
  PositionButtonGroup,
  PositionAlignButton,
  FALLBACK_FONT_FAMILIES,
  FONT_SIZE_OPTIONS,
  ANIMATION_OPTIONS,
  Body,
  designSystemToken,
  Icon,
  Button,
  TextField,
  Slider,
  Stack,
  Alignment,
  createHexInputHandlers,
};
